"use client";

import { useDeferredValue, useEffect, useState } from "react";
import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { BookOpen, Calendar, Search, User, X } from "lucide-react";
import { Pagination } from "@/components/ui/pagination";
import { Button } from "@/components/ui/button";
import { Sidebar, SidebarCard } from "@/components/sections";
import {
  getBlogPosts,
  getServiceCategories,
  type PublicBlogListingResponse,
  resolvePublicAssetUrl,
} from "@/lib/api";

const ITEMS_PER_PAGE = 12;

function formatTagLabel(tag: string) {
  return tag
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function BlogListClient({ initialData }: { initialData: PublicBlogListingResponse }) {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [page, setPage] = useState(1);
  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(searchParams.get("category"));
  const [selectedTag, setSelectedTag] = useState<string | null>(searchParams.get("tag"));
  const [selectedServiceSlug, setSelectedServiceSlug] = useState<string | null>(searchParams.get("service"));
  const [serviceOptions, setServiceOptions] = useState<Array<{ label: string; value: string }>>([]);
  const [showAllTags, setShowAllTags] = useState(false);
  const [searchQuery, setSearchQuery] = useState(searchParams.get("search") ?? "");
  const deferredSearchQuery = useDeferredValue(searchQuery);

  useEffect(() => {
    const nextParams = new URLSearchParams();
    if (selectedCategory) nextParams.set("category", selectedCategory);
    if (selectedTag) nextParams.set("tag", selectedTag);
    if (deferredSearchQuery) nextParams.set("search", deferredSearchQuery);
    if (selectedServiceSlug) nextParams.set("service", selectedServiceSlug);
    router.replace(nextParams.toString() ? `${pathname}?${nextParams.toString()}` : pathname, { scroll: false });
  }, [deferredSearchQuery, pathname, router, selectedCategory, selectedTag, selectedServiceSlug]);

  useEffect(() => {
    let active = true;
    setLoading(true);

    // Fetch service list for feature filter once
    (async () => {
      try {
        const cats: any = await getServiceCategories();
        const opts: Array<{ label: string; value: string }> = [];
        (cats || []).forEach((c: any) => {
          (c.services || []).forEach((s: any) => {
            opts.push({ label: `${s.name} — ${c.name}`, value: s.slug });
          });
        });
        setServiceOptions(opts);
      } catch (e) {
        // ignore
      }
    })();

    getBlogPosts({
      page,
      limit: ITEMS_PER_PAGE,
      category: selectedCategory || undefined,
      tag: selectedTag || undefined,
      search: deferredSearchQuery || undefined,
      serviceSlug: selectedServiceSlug || undefined,
    })
      .then((response) => {
        if (active) {
          setData(response);
        }
      })
      .catch(() => {
        if (active) {
          setData((current) => ({ ...current, posts: [] }));
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [page, selectedCategory, selectedTag, deferredSearchQuery]);
  

  const visibleTags = showAllTags ? data.popularTags : data.popularTags.slice(0, 12);
  const activeFilters = [
    selectedCategory ? { key: "category", label: `Category: ${data.categories.find((item) => item.slug === selectedCategory)?.name ?? selectedCategory}` } : null,
    selectedTag ? { key: "tag", label: `Tag: ${formatTagLabel(selectedTag)}` } : null,
    deferredSearchQuery ? { key: "search", label: `Search: ${deferredSearchQuery}` } : null,
  ].filter(Boolean) as Array<{ key: string; label: string }>;

  const clearFilters = () => {
    setSelectedCategory(null);
    setSelectedTag(null);
    setSearchQuery("");
    setPage(1);
  };

  return (
    <section className="py-14 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div>
            <div className="mb-8 space-y-5">
              <div className="relative max-w-xl">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search insights, tags, or categories"
                  value={searchQuery}
                  onChange={(event) => {
                    setSearchQuery(event.target.value);
                    setPage(1);
                  }}
                  className="w-full rounded-2xl border border-border bg-card py-3 pl-11 pr-4 text-sm text-foreground shadow-sm transition-colors placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/10"
                />
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {activeFilters.length > 0 ? (
                  <>
                    {activeFilters.map((filter) => (
                      <span key={filter.key} className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1.5 text-xs font-medium text-primary">
                        {filter.label}
                        <button
                          type="button"
                          onClick={() => {
                            if (filter.key === "category") setSelectedCategory(null);
                            if (filter.key === "tag") setSelectedTag(null);
                            if (filter.key === "search") setSearchQuery("");
                            setPage(1);
                          }}
                          className="rounded-full p-0.5 hover:bg-primary/10"
                          aria-label={`Remove ${filter.label}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                    <Button variant="ghost" size="sm" onClick={clearFilters}>Clear all</Button>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">Browse by topic, category, or author-driven research focus.</p>
                )}
              </div>

              <p className="text-sm text-muted-foreground">
                {data.pagination.total} article{data.pagination.total !== 1 ? "s" : ""}
                {selectedCategory && <> in <strong className="text-foreground">{data.categories.find((item) => item.slug === selectedCategory)?.name}</strong></>}
                {selectedTag && <> tagged <strong className="text-foreground">{formatTagLabel(selectedTag)}</strong></>}
                {deferredSearchQuery && <> matching <strong className="text-foreground">&ldquo;{deferredSearchQuery}&rdquo;</strong></>}
              </p>
              {loading && <p className="text-xs text-muted-foreground">Updating results…</p>}
            </div>

            {data.posts.length === 0 ? (
              <div className="rounded-3xl border border-dashed border-border bg-card/60 px-8 py-16 text-center">
                <BookOpen className="mx-auto mb-4 h-12 w-12 text-muted-foreground/30" />
                <h2 className="text-xl font-semibold text-foreground">No articles found</h2>
                <p className="mt-2 text-sm text-muted-foreground">Try a different category, remove a tag, or broaden your search.</p>
                <Button variant="outline" className="mt-6" onClick={clearFilters}>Reset filters</Button>
              </div>
            ) : (
              <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                {data.posts.map((post) => {
                  const previewTags = Array.isArray(post.tags)
                    ? post.tags.slice(0, 3)
                    : typeof post.tags === "string"
                    ? post.tags.split(",").map((tag) => tag.trim()).slice(0, 3)
                    : [];
                  const coverImage = resolvePublicAssetUrl(post.coverImage);

                  return (
                    <Link key={post.id} href={`/resources/insights/${post.slug}`}>
                      <article className="group flex h-full flex-col overflow-hidden rounded-3xl border border-border bg-card transition-all duration-300 hover:-translate-y-1 hover:border-primary/25 hover:shadow-xl">
                        {coverImage ? (
                          <div className="aspect-[16/10] overflow-hidden bg-muted">
                            <img src={coverImage} alt={post.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                          </div>
                        ) : (
                          <div className="flex aspect-[16/10] items-center justify-center bg-[radial-gradient(circle_at_top_left,_rgba(13,110,253,0.18),_transparent_55%),linear-gradient(135deg,rgba(13,110,253,0.05),rgba(16,185,129,0.12))]">
                            <BookOpen className="h-12 w-12 text-primary/30" />
                          </div>
                        )}
                        <div className="flex flex-1 flex-col p-6">
                          <div className="flex flex-wrap items-center gap-2">
                            {post.category && (
                              <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-emerald-700">
                                {post.category.name}
                              </span>
                            )}
                            {previewTags.map((tag) => (
                              <span key={`${post.id}-${tag}`} className="rounded-full bg-primary/10 px-2.5 py-1 text-[11px] font-medium text-primary">
                                {formatTagLabel(tag)}
                              </span>
                            ))}
                          </div>

                          <h2 className="mt-4 text-xl font-semibold leading-tight text-foreground transition-colors group-hover:text-primary line-clamp-2">
                            {post.title}
                          </h2>

                          {post.excerpt && (
                            <p className="mt-3 line-clamp-3 text-sm leading-6 text-muted-foreground">{post.excerpt}</p>
                          )}

                          <div className="mt-auto flex flex-wrap items-center gap-4 border-t border-border/60 pt-4 text-xs text-muted-foreground">
                            {post.author && (
                              <span className="flex items-center gap-1.5">
                                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-blue-600 to-cyan-500 text-white">
                                  <User className="h-3 w-3" />
                                </span>
                                {post.author}
                              </span>
                            )}
                            {post.publishedAt && (
                              <span className="flex items-center gap-1.5">
                                <Calendar className="h-3.5 w-3.5" />
                                {new Date(post.publishedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                              </span>
                            )}
                          </div>
                        </div>
                      </article>
                    </Link>
                  );
                })}
              </div>
            )}

            <Pagination currentPage={data.pagination.page} totalPages={data.pagination.totalPages} onPageChange={setPage} />
          </div>

          <Sidebar sticky>
            <SidebarCard title="Categories">
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedCategory(null);
                    setPage(1);
                  }}
                  className={`flex w-full items-center justify-between rounded-xl px-3 py-2 text-sm transition-colors ${!selectedCategory ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted"}`}
                >
                  <span>All categories</span>
                  <span>{data.categories.reduce((sum, category) => sum + category.count, 0)}</span>
                </button>
                {data.categories.map((category) => (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => {
                      setSelectedCategory(category.slug);
                      setPage(1);
                    }}
                    className={`flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm transition-colors ${selectedCategory === category.slug ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted"}`}
                  >
                    <span>{category.name}</span>
                    <span className="text-xs">{category.count}</span>
                  </button>
                ))}
              </div>
            </SidebarCard>

            <SidebarCard title="Popular Tags">
              <div className="flex flex-wrap gap-2">
                {visibleTags.map((tag) => (
                  <button
                    key={tag.tag}
                    type="button"
                    onClick={() => {
                      setSelectedTag(tag.tag);
                      setPage(1);
                    }}
                    className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${selectedTag === tag.tag ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-primary/10 hover:text-primary"}`}
                  >
                    {formatTagLabel(tag.tag)} ({tag.count})
                  </button>
                ))}
              </div>
              {data.popularTags.length > 12 && (
                <Button variant="ghost" size="sm" className="mt-3" onClick={() => setShowAllTags((current) => !current)}>
                  {showAllTags ? "Show fewer tags" : "More tags"}
                </Button>
              )}
            </SidebarCard>

            <SidebarCard title="Library Snapshot">
              <div className="space-y-3 text-sm text-muted-foreground">
                <div className="flex items-center justify-between">
                  <span>Total articles</span>
                  <strong className="text-foreground">{data.pagination.total}</strong>
                </div>
                <div className="flex items-center justify-between">
                  <span>Tracked categories</span>
                  <strong className="text-foreground">{data.categories.length}</strong>
                </div>
                <div className="flex items-center justify-between">
                  <span>Popular tags</span>
                  <strong className="text-foreground">{data.popularTags.length}</strong>
                </div>
              </div>
            </SidebarCard>
          </Sidebar>
          </div>
      </div>
    </section>
  );
}
